/**
 * GeometryDiagram - Renders SVG geometry shapes for math problems
 *
 * Usage:
 *   <GeometryDiagram type="rectangle" width={5} height={3} labels={['5 ft', '3 ft']} />
 *   <GeometryDiagram type="triangle" base={6} height={4} />
 *   <GeometryDiagram type="circle" radius={3} />
 *
 * Parsed from question text via [DIAGRAM:type,param1,param2,...] syntax
 */

import './GeometryDiagram.css';

interface BaseProps {
  className?: string;
  showLabels?: boolean;
}

interface RectangleProps extends BaseProps {
  type: 'rectangle';
  width: number;
  height: number;
  labels?: [string, string]; // [width label, height label]
  unit?: string;
}

interface TriangleProps extends BaseProps {
  type: 'triangle';
  base: number;
  height: number;
  labels?: [string, string]; // [base label, height label]
  unit?: string;
}

interface CircleProps extends BaseProps {
  type: 'circle';
  radius: number;
  labels?: [string]; // [radius label]
  unit?: string;
}

interface SquareProps extends BaseProps {
  type: 'square';
  side: number;
  labels?: [string];
  unit?: string;
}

interface LineProps extends BaseProps {
  type: 'line';
  length: number;
  labels?: [string];
  unit?: string;
}

interface AngleProps extends BaseProps {
  type: 'angle';
  degrees: number;
  labels?: [string];
}

interface ParallelogramProps extends BaseProps {
  type: 'parallelogram';
  base: number;
  height: number;
  side: number;
  unit?: string;
  labels?: [string, string, string]; // [base, height, side]
}

interface ParallelogramAngleProps extends BaseProps {
  type: 'parallelogram-angle';
  angle: number; // One angle (opposite angle is same, adjacent is 180-angle)
  labels?: string[]; // Labels for angles
}

type GeometryDiagramProps =
  | RectangleProps
  | TriangleProps
  | CircleProps
  | SquareProps
  | LineProps
  | AngleProps
  | ParallelogramProps
  | ParallelogramAngleProps;

// SVG viewBox dimensions
const SVG_WIDTH = 200;
const SVG_HEIGHT = 150;
const PADDING = 30;

export function GeometryDiagram(props: GeometryDiagramProps) {
  const { className = '' } = props;

  const renderShape = () => {
    switch (props.type) {
      case 'rectangle':
        return renderRectangle(props);
      case 'triangle':
        return renderTriangle(props);
      case 'circle':
        return renderCircle(props);
      case 'square':
        return renderSquare(props);
      case 'line':
        return renderLine(props);
      case 'angle':
        return renderAngle(props);
      case 'parallelogram':
        return renderParallelogram(props);
      case 'parallelogram-angle':
        return renderParallelogramAngle(props);
      default:
        return null;
    }
  };

  return (
    <div className={`geometry-diagram ${className}`}>
      <svg
        viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
        width="100%"
        height="auto"
        style={{ maxWidth: '300px' }}
      >
        {renderShape()}
      </svg>
    </div>
  );
}

function renderRectangle(props: RectangleProps) {
  const { width, height, labels, unit = '' } = props;

  // Scale to fit
  const maxW = SVG_WIDTH - PADDING * 2;
  const maxH = SVG_HEIGHT - PADDING * 2;
  const scale = Math.min(maxW / width, maxH / height) * 0.8;

  const rectW = width * scale;
  const rectH = height * scale;
  const x = (SVG_WIDTH - rectW) / 2;
  const y = (SVG_HEIGHT - rectH) / 2;

  const widthLabel = labels?.[0] || `${width}${unit ? ' ' + unit : ''}`;
  const heightLabel = labels?.[1] || `${height}${unit ? ' ' + unit : ''}`;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={rectW}
        height={rectH}
        fill="none"
        stroke="#4F46E5"
        strokeWidth="2"
      />
      {/* Width label (bottom) */}
      <text x={x + rectW / 2} y={y + rectH + 18} textAnchor="middle" className="diagram-label">
        {widthLabel}
      </text>
      {/* Height label (right) */}
      <text x={x + rectW + 10} y={y + rectH / 2} textAnchor="start" dominantBaseline="middle" className="diagram-label">
        {heightLabel}
      </text>
      {/* Right angle markers */}
      <path d={`M ${x + 10} ${y} L ${x + 10} ${y + 10} L ${x} ${y + 10}`} fill="none" stroke="#4F46E5" strokeWidth="1" />
    </g>
  );
}

function renderTriangle(props: TriangleProps) {
  const { base, height, labels, unit = '' } = props;

  const maxW = SVG_WIDTH - PADDING * 2;
  const maxH = SVG_HEIGHT - PADDING * 2;
  const scale = Math.min(maxW / base, maxH / height) * 0.8;

  const triW = base * scale;
  const triH = height * scale;
  const startX = (SVG_WIDTH - triW) / 2;
  const startY = SVG_HEIGHT - PADDING;

  const baseLabel = labels?.[0] || `${base}${unit ? ' ' + unit : ''}`;
  const heightLabel = labels?.[1] || `${height}${unit ? ' ' + unit : ''}`;

  // Right triangle (base on bottom, right angle at bottom-left)
  const points = `${startX},${startY} ${startX + triW},${startY} ${startX},${startY - triH}`;

  return (
    <g>
      <polygon
        points={points}
        fill="none"
        stroke="#4F46E5"
        strokeWidth="2"
      />
      {/* Base label */}
      <text x={startX + triW / 2} y={startY + 18} textAnchor="middle" className="diagram-label">
        {baseLabel}
      </text>
      {/* Height label */}
      <text x={startX - 10} y={startY - triH / 2} textAnchor="end" dominantBaseline="middle" className="diagram-label">
        {heightLabel}
      </text>
      {/* Height dashed line */}
      <line x1={startX} y1={startY} x2={startX} y2={startY - triH} stroke="#999" strokeWidth="1" strokeDasharray="4" />
      {/* Right angle marker */}
      <path d={`M ${startX + 8} ${startY} L ${startX + 8} ${startY - 8} L ${startX} ${startY - 8}`} fill="none" stroke="#4F46E5" strokeWidth="1" />
    </g>
  );
}

function renderCircle(props: CircleProps) {
  const { radius, labels, unit = '' } = props;

  const maxR = Math.min(SVG_WIDTH, SVG_HEIGHT) / 2 - PADDING;
  const scale = maxR / radius * 0.8;
  const r = radius * scale;

  const cx = SVG_WIDTH / 2;
  const cy = SVG_HEIGHT / 2;

  const radiusLabel = labels?.[0] || `r = ${radius}${unit ? ' ' + unit : ''}`;

  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke="#4F46E5"
        strokeWidth="2"
      />
      {/* Center dot */}
      <circle cx={cx} cy={cy} r="3" fill="#4F46E5" />
      {/* Radius line */}
      <line x1={cx} y1={cy} x2={cx + r} y2={cy} stroke="#4F46E5" strokeWidth="1.5" />
      {/* Radius label */}
      <text x={cx + r / 2} y={cy - 8} textAnchor="middle" className="diagram-label">
        {radiusLabel}
      </text>
    </g>
  );
}

function renderSquare(props: SquareProps) {
  const { side, labels, unit = '' } = props;

  const maxS = Math.min(SVG_WIDTH, SVG_HEIGHT) - PADDING * 2;
  const scale = maxS / side * 0.7;
  const s = side * scale;

  const x = (SVG_WIDTH - s) / 2;
  const y = (SVG_HEIGHT - s) / 2;

  const sideLabel = labels?.[0] || `${side}${unit ? ' ' + unit : ''}`;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={s}
        height={s}
        fill="none"
        stroke="#4F46E5"
        strokeWidth="2"
      />
      {/* Side label */}
      <text x={x + s / 2} y={y + s + 18} textAnchor="middle" className="diagram-label">
        {sideLabel}
      </text>
      {/* Equal marks on sides */}
      <line x1={x + s / 2 - 5} y1={y - 3} x2={x + s / 2 + 5} y2={y - 3} stroke="#4F46E5" strokeWidth="1.5" />
      <line x1={x + s + 3} y1={y + s / 2 - 5} x2={x + s + 3} y2={y + s / 2 + 5} stroke="#4F46E5" strokeWidth="1.5" />
    </g>
  );
}

function renderLine(props: LineProps) {
  const { length, labels, unit = '' } = props;

  const maxL = SVG_WIDTH - PADDING * 2;
  const scale = maxL / length * 0.8;
  const l = length * scale;

  const startX = (SVG_WIDTH - l) / 2;
  const y = SVG_HEIGHT / 2;

  const lengthLabel = labels?.[0] || `${length}${unit ? ' ' + unit : ''}`;

  return (
    <g>
      <line x1={startX} y1={y} x2={startX + l} y2={y} stroke="#4F46E5" strokeWidth="2" />
      {/* End markers */}
      <line x1={startX} y1={y - 8} x2={startX} y2={y + 8} stroke="#4F46E5" strokeWidth="2" />
      <line x1={startX + l} y1={y - 8} x2={startX + l} y2={y + 8} stroke="#4F46E5" strokeWidth="2" />
      {/* Label */}
      <text x={startX + l / 2} y={y - 12} textAnchor="middle" className="diagram-label">
        {lengthLabel}
      </text>
    </g>
  );
}

function renderAngle(props: AngleProps) {
  const { degrees, labels } = props;

  const cx = SVG_WIDTH / 2;
  const cy = SVG_HEIGHT / 2 + 20;
  const lineLength = 60;

  // Convert degrees to radians
  const radians = (degrees * Math.PI) / 180;

  // Calculate end point of angled line
  const endX = cx + Math.cos(radians) * lineLength;
  const endY = cy - Math.sin(radians) * lineLength;

  const angleLabel = labels?.[0] || `${degrees}°`;

  // Arc for angle marker
  const arcRadius = 25;
  const arcEndX = cx + Math.cos(radians) * arcRadius;
  const arcEndY = cy - Math.sin(radians) * arcRadius;
  const largeArc = degrees > 180 ? 1 : 0;

  return (
    <g>
      {/* Base line (horizontal) */}
      <line x1={cx} y1={cy} x2={cx + lineLength} y2={cy} stroke="#4F46E5" strokeWidth="2" />
      {/* Angled line */}
      <line x1={cx} y1={cy} x2={endX} y2={endY} stroke="#4F46E5" strokeWidth="2" />
      {/* Angle arc */}
      <path
        d={`M ${cx + arcRadius} ${cy} A ${arcRadius} ${arcRadius} 0 ${largeArc} 0 ${arcEndX} ${arcEndY}`}
        fill="none"
        stroke="#4F46E5"
        strokeWidth="1.5"
      />
      {/* Angle label */}
      <text
        x={cx + arcRadius + 15}
        y={cy - 10}
        textAnchor="start"
        className="diagram-label"
      >
        {angleLabel}
      </text>
    </g>
  );
}

function renderParallelogram(props: ParallelogramProps) {
  const { base, height, side, labels, unit = '' } = props;

  const maxW = SVG_WIDTH - PADDING * 2;
  const maxH = SVG_HEIGHT - PADDING * 2;

  // Calculate slant offset (how far the top is shifted)
  const slantOffset = Math.sqrt(side * side - height * height) || side * 0.3;
  const totalWidth = base + slantOffset;

  const scale = Math.min(maxW / totalWidth, maxH / height) * 0.7;

  const scaledBase = base * scale;
  const scaledHeight = height * scale;
  const scaledSlant = slantOffset * scale;

  const startX = (SVG_WIDTH - scaledBase - scaledSlant) / 2;
  const bottomY = SVG_HEIGHT - PADDING;
  const topY = bottomY - scaledHeight;

  // Four corners: bottom-left, bottom-right, top-right, top-left
  const points = `${startX},${bottomY} ${startX + scaledBase},${bottomY} ${startX + scaledBase + scaledSlant},${topY} ${startX + scaledSlant},${topY}`;

  const baseLabel = labels?.[0] || `${base}${unit ? ' ' + unit : ''}`;
  const heightLabel = labels?.[1] || `${height}${unit ? ' ' + unit : ''}`;
  const sideLabel = labels?.[2] || `${side}${unit ? ' ' + unit : ''}`;

  return (
    <g>
      <polygon
        points={points}
        fill="none"
        stroke="#4F46E5"
        strokeWidth="2"
      />
      {/* Base label (bottom) */}
      <text x={startX + scaledBase / 2} y={bottomY + 18} textAnchor="middle" className="diagram-label">
        {baseLabel}
      </text>
      {/* Height dashed line */}
      <line
        x1={startX + scaledBase}
        y1={bottomY}
        x2={startX + scaledBase}
        y2={topY}
        stroke="#999"
        strokeWidth="1"
        strokeDasharray="4"
      />
      {/* Height label */}
      <text x={startX + scaledBase + 12} y={(bottomY + topY) / 2} textAnchor="start" dominantBaseline="middle" className="diagram-label">
        {heightLabel}
      </text>
      {/* Side label (left slanted side) */}
      <text x={startX + scaledSlant / 2 - 15} y={(bottomY + topY) / 2} textAnchor="end" dominantBaseline="middle" className="diagram-label">
        {sideLabel}
      </text>
      {/* Right angle marker at height line */}
      <path
        d={`M ${startX + scaledBase - 8} ${bottomY} L ${startX + scaledBase - 8} ${bottomY - 8} L ${startX + scaledBase} ${bottomY - 8}`}
        fill="none"
        stroke="#4F46E5"
        strokeWidth="1"
      />
    </g>
  );
}

function renderParallelogramAngle(props: ParallelogramAngleProps) {
  const { angle, labels } = props;

  const cx = SVG_WIDTH / 2;
  const cy = SVG_HEIGHT / 2;
  const baseLength = 80;
  const height = 50;
  const slant = 25;

  // Parallelogram corners
  const bl = { x: cx - baseLength / 2, y: cy + height / 2 }; // bottom-left
  const br = { x: cx + baseLength / 2, y: cy + height / 2 }; // bottom-right
  const tr = { x: cx + baseLength / 2 + slant, y: cy - height / 2 }; // top-right
  const tl = { x: cx - baseLength / 2 + slant, y: cy - height / 2 }; // top-left

  const points = `${bl.x},${bl.y} ${br.x},${br.y} ${tr.x},${tr.y} ${tl.x},${tl.y}`;

  // The given angle is at bottom-left, opposite at top-right is same
  // Adjacent angles (bottom-right, top-left) are 180 - angle
  const oppositeAngle = angle;
  const adjacentAngle = 180 - angle;

  // Default labels
  const angleLabels = labels || [`${angle}°`, `${adjacentAngle}°`, `${oppositeAngle}°`, `${adjacentAngle}°`];

  return (
    <g>
      <polygon
        points={points}
        fill="none"
        stroke="#4F46E5"
        strokeWidth="2"
      />
      {/* Bottom-left angle (given angle) */}
      <text x={bl.x + 15} y={bl.y - 10} textAnchor="start" className="diagram-label">
        {angleLabels[0]}
      </text>
      {/* Bottom-right angle (adjacent) */}
      <text x={br.x - 15} y={br.y - 10} textAnchor="end" className="diagram-label">
        {angleLabels[1]}
      </text>
      {/* Top-right angle (opposite) */}
      <text x={tr.x - 15} y={tr.y + 18} textAnchor="end" className="diagram-label">
        {angleLabels[2]}
      </text>
      {/* Top-left angle (adjacent) */}
      <text x={tl.x + 15} y={tl.y + 18} textAnchor="start" className="diagram-label">
        {angleLabels[3]}
      </text>
    </g>
  );
}

/**
 * Parse [DIAGRAM:type,param1,param2,...] syntax from text
 */
export function parseDiagramCommands(text: string): {
  text: string;
  diagrams: GeometryDiagramProps[];
} {
  const diagramPattern = /\[DIAGRAM:([^\]]+)\]/g;
  const diagrams: GeometryDiagramProps[] = [];

  const cleanText = text.replace(diagramPattern, (_, params) => {
    const parts = params.split(',').map((p: string) => p.trim());
    const type = parts[0]?.toLowerCase();

    switch (type) {
      case 'rectangle':
        diagrams.push({
          type: 'rectangle',
          width: parseFloat(parts[1]) || 1,
          height: parseFloat(parts[2]) || 1,
          unit: parts[3] || '',
        });
        break;
      case 'triangle':
        diagrams.push({
          type: 'triangle',
          base: parseFloat(parts[1]) || 1,
          height: parseFloat(parts[2]) || 1,
          unit: parts[3] || '',
        });
        break;
      case 'circle':
        diagrams.push({
          type: 'circle',
          radius: parseFloat(parts[1]) || 1,
          unit: parts[2] || '',
        });
        break;
      case 'square':
        diagrams.push({
          type: 'square',
          side: parseFloat(parts[1]) || 1,
          unit: parts[2] || '',
        });
        break;
      case 'line':
        diagrams.push({
          type: 'line',
          length: parseFloat(parts[1]) || 1,
          unit: parts[2] || '',
        });
        break;
      case 'angle':
        diagrams.push({
          type: 'angle',
          degrees: parseFloat(parts[1]) || 90,
        });
        break;
      case 'parallelogram':
        diagrams.push({
          type: 'parallelogram',
          base: parseFloat(parts[1]) || 1,
          height: parseFloat(parts[2]) || 1,
          side: parseFloat(parts[3]) || 1,
          unit: parts[4] || '',
        });
        break;
      case 'parallelogram-angle':
        diagrams.push({
          type: 'parallelogram-angle',
          angle: parseFloat(parts[1]) || 75,
        });
        break;
    }
    return ''; // Remove from text
  });

  return {
    text: cleanText.trim(),
    diagrams,
  };
}

export default GeometryDiagram;
